import Shepherd from "shepherd.js";

/** Misma ruta canonical que `<Logo variant="wordmark" format="png" />`; fallback a SVG existente si el PNG no está en `/public`. */
const AURORA_TOUR_PUBLIC_LOGO_PRIMARY = "/assets/logo/logo-wordmark.png";
const AURORA_TOUR_PUBLIC_LOGO_FALLBACK_ICON = "/assets/logo/logo-icon.svg";

function auroraTourPublicAssetUrl(pathname) {
  const trimmed = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const pub =
    typeof process !== "undefined" && process.env && typeof process.env.PUBLIC_URL === "string"
      ? process.env.PUBLIC_URL.replace(/\/$/, "")
      : "";
  return `${pub}${trimmed}`;
}

/**
 * Header del primer y último paso: logo real + texto (sin emojis como título solo).
 * @param {string} titleText — Subtítulo bajo el wordmark/isotipo
 * @returns {HTMLDivElement}
 */
function createShepherdTitleWithLogo(titleText) {
  const wrap = document.createElement("div");
  wrap.style.display = "flex";
  wrap.style.flexDirection = "column";
  wrap.style.alignItems = "flex-start";
  wrap.style.textAlign = "left";
  wrap.style.gap = "0.375rem";
  wrap.style.margin = "0";
  wrap.style.padding = "0";

  const img = document.createElement("img");
  img.src = auroraTourPublicAssetUrl(AURORA_TOUR_PUBLIC_LOGO_PRIMARY);
  img.alt = "Aurora Geriátrica";
  img.style.display = "block";
  img.style.height = "1.875rem";
  img.style.width = "auto";
  img.style.objectFit = "contain";
  img.style.objectPosition = "left center";
  img.style.margin = "0";
  img.onerror = () => {
    const fallbackSrc = auroraTourPublicAssetUrl(AURORA_TOUR_PUBLIC_LOGO_FALLBACK_ICON);
    if (img.src.endsWith(".svg")) {
      img.onerror = null;
      img.style.height = "1.75rem";
      return;
    }
    img.src = fallbackSrc;
  };

  wrap.appendChild(img);

  if (titleText) {
    const span = document.createElement("span");
    span.textContent = titleText;
    span.style.display = "block";
    span.style.fontSize = "0.875rem";
    span.style.fontWeight = "600";
    span.style.lineHeight = "1.35";
    span.style.color = "#ffffff";
    wrap.appendChild(span);
  }

  return wrap;
}

export const AURORA_TOUR_KEYS = {
  dashboard: "aurora_tour_dashboard",
  consulta: "aurora_tour_consulta",
  pdf: "aurora_tour_pdf",
  personalizacion: "aurora_tour_personalizacion",
  descarga: "aurora_tour_descarga",
};

export const ALL_AURORA_TOUR_KEYS = Object.values(AURORA_TOUR_KEYS);

export function hasSeenAuroraTour(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

export function markAuroraTourSeen(storageKey) {
  try {
    localStorage.setItem(storageKey, "1");
  } catch {
    /* ignore */
  }
}

export function clearAllAuroraTours() {
  ALL_AURORA_TOUR_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

function buildStepButtons(tour, index, total) {
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const buttons = [];

  if (!isFirst) {
    buttons.push({
      text: "Anterior",
      classes: "shepherd-button-secondary",
      action: () => tour.back(),
    });
  }

  buttons.push({
    text: isLast ? "Entendido" : "Siguiente",
    classes: "shepherd-button-primary",
    action: () => {
      if (isLast) tour.complete();
      else tour.next();
    },
  });

  return buttons;
}

/**
 * @param {{ storageKey: string, steps: Array<{ id: string, title?: string, text: string, brandHeader?: boolean, attachTo?: { element: string, on?: string } }>, onComplete?: () => void }} options
 * @returns {import('shepherd.js').Tour | null}
 */
export function startAuroraTour({ storageKey, steps, onComplete }) {
  if (hasSeenAuroraTour(storageKey) || !steps?.length) return null;

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: "smooth", block: "center" },
      classes: "aurora-shepherd-step",
    },
  });

  steps.forEach((step, index) => {
    const baseTitleStr = step.title || "Aurora Geriátrica";

    const stepOptions = {
      id: step.id,
      title: step.brandHeader ? createShepherdTitleWithLogo(baseTitleStr) : baseTitleStr,
      text: step.text,
      buttons: buildStepButtons(tour, index, steps.length),
    };
    if (step.attachTo?.element) {
      const el = document.querySelector(step.attachTo.element);
      if (el) {
        stepOptions.attachTo = {
          element: step.attachTo.element,
          on: step.attachTo.on || "bottom",
        };
      }
    }
    tour.addStep(stepOptions);
  });

  tour.on("complete", () => {
    markAuroraTourSeen(storageKey);
    onComplete?.();
  });

  tour.on("cancel", () => {
    onComplete?.();
  });

  tour.start();
  return tour;
}

export function waitForTourTargets(selectors, { timeoutMs = 8000, intervalMs = 200 } = {}) {
  return new Promise((resolve) => {
    const needed = selectors.filter(Boolean);
    if (!needed.length) {
      resolve(true);
      return;
    }
    const started = Date.now();
    const tick = () => {
      const ready = needed.every((sel) => document.querySelector(sel));
      if (ready) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });
}
